import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import EmptyState from "@/components/base/EmptyState";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_STATUT_LABELS,
  AGENCE_BIEN_STATUT_TONES,
  AGENCE_BIEN_TYPE_ICONS,
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_CONTRAT_STATUT_LABELS,
  AGENCE_CONTRAT_STATUT_TONES,
  agenceContratReference,
  agenceLocataireNomComplet,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import BienForm from "../components/BienForm";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";
import { formatDate, formatFcfa } from "@/utils/format";

export default function AgenceBienDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { getBien, getImmeuble, contratsByBien, getLocataire, removeBien } = useAgenceData();
  const { getProprietaire } = useProprietaires();
  const { can } = useEmployes();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [toDelete, setToDelete] = useState(false);
  const bien = id ? getBien(id) : undefined;

  if (!bien) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to="/agence/biens"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux biens
        </Link>
        <EmptyState
          icon="ri-home-4-line"
          title="Bien introuvable"
          description="Ce bien n'existe pas ou n'est plus disponible. Retournez à la liste pour le retrouver."
          action={
            <Link
              to="/agence/biens"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les biens
            </Link>
          }
        />
      </div>
    );
  }

  const immeuble = getImmeuble(bien.immeubleId);
  const proprietaire = getProprietaire(bien.proprietaireId);
  const contrats = contratsByBien(bien.id);
  const contratActif = contrats.find((contrat) => contrat.statut === "actif");
  const occupant = contratActif ? getLocataire(contratActif.locataireId) : undefined;
  const typeLabel = AGENCE_BIEN_TYPE_LABELS[bien.type];

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Type", value: typeLabel, icon: AGENCE_BIEN_TYPE_ICONS[bien.type] },
    { label: "Référence", value: bien.reference || "—", icon: "ri-price-tag-3-line" },
    { label: "Propriétaire", value: proprietaire ? proprietaireNomComplet(proprietaire) : "—", icon: "ri-user-star-line" },
    { label: "Immeuble", value: immeuble ? immeuble.nom : "—", icon: "ri-building-2-line" },
    { label: "Étage", value: bien.etage || "—", icon: "ri-stack-line" },
    { label: "Numéro du bien", value: bien.numero || "—", icon: "ri-hashtag" },
    { label: "Surface", value: bien.surface > 0 ? `${bien.surface} m²` : "—", icon: "ri-ruler-2-line" },
    { label: "Loyer mensuel", value: formatFcfa(bien.loyerMensuel), icon: "ri-wallet-3-line" },
    { label: "Statut", value: AGENCE_BIEN_STATUT_LABELS[bien.statut], icon: "ri-checkbox-circle-line" },
    {
      label: "Locataire actuel",
      value: occupant ? agenceLocataireNomComplet(occupant) : "Aucun contrat actif",
      icon: "ri-user-3-line",
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/agence/biens"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux biens
      </Link>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <i className={`${AGENCE_BIEN_TYPE_ICONS[bien.type]} text-2xl`} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={AGENCE_BIEN_STATUT_TONES[bien.statut]}>{AGENCE_BIEN_STATUT_LABELS[bien.statut]}</Badge>
              <Badge tone="secondary" icon="ri-home-4-line">
                {typeLabel}
              </Badge>
              {proprietaire && (
                <Badge tone="primary" icon="ri-user-star-line">
                  {proprietaireNomComplet(proprietaire)}
                </Badge>
              )}
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
              {bien.reference || typeLabel}
            </h2>
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-foreground-600">
              <i className="ri-building-2-line text-base text-foreground-400" aria-hidden="true" />
              <span className="truncate">{immeuble ? immeuble.nom : "Immeuble non renseigné"}</span>
            </p>
            <p className="mt-1 text-xs text-foreground-400">Ajouté le {formatDate(bien.createdAt)}</p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
          <Link
            to={`/agence/contrats?bien=${bien.id}&nouveau=1`}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-file-add-line text-base" aria-hidden="true" />
            Créer un contrat
          </Link>
          {immeuble && (
            <Link
              to={`/agence/immeubles/${immeuble.id}`}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-building-2-line text-base" aria-hidden="true" />
              Voir l'immeuble
            </Link>
          )}
          {can("biens.modifier") && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-edit-line text-base" aria-hidden="true" />
              Modifier
            </button>
          )}
          {can("biens.supprimer") && (
            <button
              type="button"
              onClick={() => setToDelete(true)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
            >
              <i className="ri-delete-bin-line text-base" aria-hidden="true" />
              Supprimer
            </button>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-information-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
              Informations du bien
            </h3>
          </header>
          <div className="p-4 md:p-5">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {infos.map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                    <i className={`${info.icon} text-base`} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-500">{info.label}</dt>
                    <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            <div className="mt-5 border-t border-background-200 pt-4">
              <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                Éléments liés
              </p>
              <div className="mt-2 flex flex-col gap-2">
                {proprietaire && (
                  <Link
                    to={`/agence/proprietaires/${proprietaire.id}`}
                    className="link-arrow flex items-center gap-3 rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2.5 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                      <i className="ri-user-star-line text-base" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] text-foreground-500">Propriétaire</span>
                      <span className="block truncate text-sm font-label font-medium text-foreground-950">
                        {proprietaireNomComplet(proprietaire)}
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
                  </Link>
                )}
                {immeuble && (
                  <Link
                    to={`/agence/immeubles/${immeuble.id}`}
                    className="link-arrow flex items-center gap-3 rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2.5 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                      <i className="ri-building-2-line text-base" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] text-foreground-500">Immeuble</span>
                      <span className="block truncate text-sm font-label font-medium text-foreground-950">
                        {immeuble.nom}
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
                  </Link>
                )}
              </div>
            </div>

            {bien.description && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Description
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{bien.description}</p>
              </div>
            )}

            {bien.infosComplementaires && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Informations complémentaires
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{bien.infosComplementaires}</p>
              </div>
            )}
          </div>
        </section>

        <section className="flex h-fit flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-links-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Accès rapides</h3>
          </header>
          <div className="flex flex-col gap-1 p-2">
            {[
              { label: "Contrats", to: `/agence/contrats?bien=${bien.id}`, icon: "ri-file-text-line", hint: "Baux de ce bien" },
              { label: "Immeuble", to: immeuble ? `/agence/immeubles/${immeuble.id}` : "/agence/immeubles", icon: "ri-building-2-line", hint: "Immeuble du bien" },
              { label: "Locataires", to: "/agence/locataires", icon: "ri-user-3-line", hint: "Fiches des occupants" },
              { label: "Loyers", to: `/agence/loyers?bien=${bien.id}`, icon: "ri-wallet-3-line", hint: "Suivi par période" },
              { label: "Maintenance", to: `/agence/maintenance?bien=${bien.id}`, icon: "ri-tools-line", hint: "Demandes d'entretien" },
              { label: "Documents", to: `/agence/documents?bien=${bien.id}`, icon: "ri-folder-line", hint: "Pièces et justificatifs" },
            ].map((module) => (
              <Link
                key={module.to}
                to={module.to}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className={`${module.icon} text-base`} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-label font-medium text-foreground-950">
                    {module.label}
                  </span>
                  <span className="block truncate text-xs text-foreground-500">{module.hint}</span>
                </span>
                <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>
      </div>

      <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
        <header className="flex items-center justify-between gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
          <span className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
              <i className="ri-file-text-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Contrats du bien</h3>
          </span>
          <span className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-label font-medium text-foreground-600">
            {contrats.length}
          </span>
        </header>
        <div className="p-4 md:p-5">
          {contrats.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-8 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-primary-600">
                <i className="ri-file-text-line text-xl" aria-hidden="true" />
              </span>
              <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">Aucun contrat pour ce bien</p>
              <p className="mt-1 max-w-xs text-xs text-foreground-600">
                Créez un contrat pour relier un locataire à ce bien.
              </p>
              <Link
                to={`/agence/contrats?bien=${bien.id}&nouveau=1`}
                className="mt-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-add-line text-base" aria-hidden="true" />
                Créer un contrat
              </Link>
            </div>
          ) : (
            <ul className="flex flex-col gap-3">
              {contrats.map((contrat) => {
                const locataire = getLocataire(contrat.locataireId);
                return (
                  <li key={contrat.id}>
                    <Link
                      to={`/agence/contrats/${contrat.id}`}
                      className="flex flex-col gap-2 rounded-md border border-background-200 p-3.5 transition-colors hover:bg-background-100 sm:flex-row sm:items-center sm:justify-between cursor-pointer"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-label font-medium text-foreground-950">
                          {agenceContratReference(contrat.id)} ·{" "}
                          {locataire ? agenceLocataireNomComplet(locataire) : "Locataire non renseigné"}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-foreground-500">
                          Du {formatDate(contrat.dateDebut)}
                          {contrat.dateFin ? ` au ${formatDate(contrat.dateFin)}` : ""} ·{" "}
                          {formatFcfa(contrat.loyerMensuel)} / mois
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <Badge tone={AGENCE_CONTRAT_STATUT_TONES[contrat.statut]}>
                          {AGENCE_CONTRAT_STATUT_LABELS[contrat.statut]}
                        </Badge>
                        <i className="ri-arrow-right-s-line text-base text-foreground-400" aria-hidden="true" />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <BienForm open={editOpen} initial={bien} onClose={() => setEditOpen(false)} />

      <ConfirmDeleteModal
        open={toDelete}
        onClose={() => setToDelete(false)}
        onConfirm={() => {
          removeBien(bien.id);
          navigate("/agence/biens");
        }}
        title="Supprimer le bien"
        description={`Voulez-vous vraiment supprimer « ${bien.reference || AGENCE_BIEN_TYPE_LABELS[bien.type]} » ? Cette action est définitive.`}
        blockedMessage={
          contratsByBien(bien.id).length > 0
            ? `Ce bien est relié à ${contratsByBien(bien.id).length} contrat(s). Retirez ou clôturez d'abord ses contrats.`
            : undefined
        }
      />
    </div>
  );
}